import { mount, Router, navigate, configureRouter } from 'onefold';
import { AppShell } from './layout/AppShell';

// Force hash-based routing for static hosting (GitHub Pages)
configureRouter({ hash: true });

// Route definitions
import { IntroPage } from './pages/getting-started/IntroPage';
import { InstallPage } from './pages/getting-started/InstallPage';
import { QuickStartPage } from './pages/getting-started/QuickStartPage';
import { SignalsPage } from './pages/core/SignalsPage';
import { TemplatesPage } from './pages/core/TemplatesPage';
import { CssPage } from './pages/core/CssPage';
import { MountingPage } from './pages/core/MountingPage';
import { RouterPage } from './pages/routing/RouterPage';
import { ConfigureRouterPage } from './pages/routing/ConfigureRouterPage';
import { NestedRoutesPage } from './pages/routing/NestedRoutesPage';
import { NavigatePage } from './pages/routing/NavigatePage';
import { LinkPage } from './pages/routing/LinkPage';
import { DynamicParamsPage } from './pages/routing/DynamicParamsPage';
import { StorePage } from './pages/state/StorePage';
import { PersistedPage } from './pages/state/PersistedPage';
import { ResourcePage } from './pages/data/ResourcePage';
import { HttpClientPage } from './pages/data/HttpClientPage';
import { InterceptorsPage } from './pages/data/InterceptorsPage';
import { FormsPage } from './pages/forms/FormsPage';
import { ValidationPage } from './pages/forms/ValidationPage';
import { SecurityPage as MfeSecurityPage } from './pages/microfrontends/SecurityPage';
import { LoadRemotePage as MfeLoadRemotePage } from './pages/microfrontends/LoadRemotePage';
import { IsolationPage as MfeIsolationPage } from './pages/microfrontends/IsolationPage';
import { CommunicationPage as MfeCommunicationPage } from './pages/microfrontends/CommunicationPage';
import { SriPage as MfeSriPage } from './pages/microfrontends/SriPage';
import { DeploymentPage as MfeDeploymentPage } from './pages/microfrontends/DeploymentPage';
import { SharedDepsPage as MfeSharedDepsPage } from './pages/microfrontends/SharedDepsPage';
import { CrossFrameworkPage as MfeCrossFrameworkPage } from './pages/microfrontends/CrossFrameworkPage';
import { ApiReferencePage as MfeApiReferencePage } from './pages/microfrontends/ApiReferencePage';
import { SuspensePage } from './pages/async/SuspensePage';
import { LazyLoadingPage } from './pages/async/LazyLoadingPage';
import { ErrorBoundariesPage } from './pages/async/ErrorBoundariesPage';
import { WebSocketPage } from './pages/streaming/WebSocketPage';
import { SsePage } from './pages/streaming/SsePage';
import { I18nPage } from './pages/i18n/I18nPage';
import { ThemePage } from './pages/theming/ThemePage';
import { A11yPage } from './pages/a11y/A11yPage';
import { TransitionsPage } from './pages/transitions/TransitionsPage';
import { DiPage } from './pages/di/DiPage';
import { GuardsPage } from './pages/security/GuardsPage';
import { XssPage } from './pages/security/XssPage';
import { VirtualListPage } from './pages/performance/VirtualListPage';
import { CodeSplittingPage } from './pages/performance/CodeSplittingPage';
import { WrapImperativePage } from './pages/interop/WrapImperativePage';
import { EmbedForeignPage } from './pages/interop/EmbedForeignPage';
import { PluginsPage } from './pages/plugins/PluginsPage';
import { ObservabilityPage } from './pages/observability/ObservabilityPage';
import { ComponentMetaPage } from './pages/meta/ComponentMetaPage';
import { SsrPage } from './pages/ssr/SsrPage';
import { DevToolsPage } from './pages/devtools/DevToolsPage';
import { UtilitiesPage } from './pages/utilities/UtilitiesPage';
import { ExtensionsPage } from './pages/extensions/ExtensionsPage';
import { CliPage } from './pages/cli/CliPage';
import { PlaygroundPage } from './pages/playground/PlaygroundPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { GithubPagesPage } from './pages/deployment/GithubPagesPage';
import { VercelPage } from './pages/deployment/VercelPage';
import { NetlifyPage } from './pages/deployment/NetlifyPage';
import { CloudflarePage } from './pages/deployment/CloudflarePage';
import { AwsPage } from './pages/deployment/AwsPage';
import { DockerPage } from './pages/deployment/DockerPage';

/** Pass-through parent view — renders the matched child directly. */
const section = (_params: Record<string, string>, outlet?: Node) => outlet ?? NotFoundPage();

const routes = [
  // Home
  { path: '/', view: () => IntroPage() },

  // Getting Started
  { path: '/getting-started', view: section, children: [
    { path: '/install', view: () => InstallPage() },
    { path: '/quickstart', view: () => QuickStartPage() },
  ]},

  // Core Fundamentals
  { path: '/core', view: section, children: [
    { path: '/signals', view: () => SignalsPage() },
    { path: '/templates', view: () => TemplatesPage() },
    { path: '/css', view: () => CssPage() },
    { path: '/mounting', view: () => MountingPage() },
  ]},

  // Routing
  { path: '/routing', view: section, children: [
    { path: '/router', view: () => RouterPage() },
    { path: '/configure', view: () => ConfigureRouterPage() },
    { path: '/nested', view: () => NestedRoutesPage() },
    { path: '/navigate', view: () => NavigatePage() },
    { path: '/link', view: () => LinkPage() },
    { path: '/params', view: () => DynamicParamsPage() },
  ]},

  // State Management
  { path: '/state', view: section, children: [
    { path: '/store', view: () => StorePage() },
    { path: '/persisted', view: () => PersistedPage() },
  ]},

  // Data Fetching
  { path: '/data', view: section, children: [
    { path: '/resource', view: () => ResourcePage() },
    { path: '/http-client', view: () => HttpClientPage() },
    { path: '/interceptors', view: () => InterceptorsPage() },
  ]},

  // Forms
  { path: '/forms', view: section, children: [
    { path: '/create-form', view: () => FormsPage() },
    { path: '/validation', view: () => ValidationPage() },
  ]},

  // Microfrontends
  { path: '/microfrontends', view: section, children: [
    { path: '/load-remote', view: () => MfeLoadRemotePage() },
    { path: '/isolation', view: () => MfeIsolationPage() },
    { path: '/communication', view: () => MfeCommunicationPage() },
    { path: '/security', view: () => MfeSecurityPage() },
    { path: '/sri', view: () => MfeSriPage() },
    { path: '/shared-deps', view: () => MfeSharedDepsPage() },
    { path: '/cross-framework', view: () => MfeCrossFrameworkPage() },
    { path: '/deployment', view: () => MfeDeploymentPage() },
    { path: '/api-reference', view: () => MfeApiReferencePage() },
  ]},

  // Async Patterns
  { path: '/async', view: section, children: [
    { path: '/suspense', view: () => SuspensePage() },
    { path: '/lazy-loading', view: () => LazyLoadingPage() },
    { path: '/error-boundaries', view: () => ErrorBoundariesPage() },
  ]},

  // Streaming
  { path: '/streaming', view: section, children: [
    { path: '/websocket', view: () => WebSocketPage() },
    { path: '/sse', view: () => SsePage() },
  ]},

  // Security
  { path: '/security', view: section, children: [
    { path: '/guards', view: () => GuardsPage() },
    { path: '/xss', view: () => XssPage() },
  ]},

  // Performance
  { path: '/performance', view: section, children: [
    { path: '/virtual-list', view: () => VirtualListPage() },
    { path: '/code-splitting', view: () => CodeSplittingPage() },
  ]},

  // Interop
  { path: '/interop', view: section, children: [
    { path: '/wrap-imperative', view: () => WrapImperativePage() },
    { path: '/embed-foreign', view: () => EmbedForeignPage() },
  ]},

  // Single-page sections (no sub-routes)
  { path: '/i18n', view: () => I18nPage() },
  { path: '/theming', view: () => ThemePage() },
  { path: '/a11y', view: () => A11yPage() },
  { path: '/transitions', view: () => TransitionsPage() },
  { path: '/di', view: () => DiPage() },
  { path: '/plugins', view: () => PluginsPage() },
  { path: '/observability', view: () => ObservabilityPage() },
  { path: '/meta', view: () => ComponentMetaPage() },
  { path: '/ssr', view: () => SsrPage() },
  { path: '/devtools', view: () => DevToolsPage() },
  { path: '/utilities', view: () => UtilitiesPage() },
  { path: '/extensions', view: () => ExtensionsPage() },
  { path: '/cli', view: () => CliPage() },
  { path: '/playground', view: () => PlaygroundPage() },

  // Deployment
  { path: '/deployment', view: section, children: [
    { path: '/github-pages', view: () => GithubPagesPage() },
    { path: '/vercel', view: () => VercelPage() },
    { path: '/netlify', view: () => NetlifyPage() },
    { path: '/cloudflare', view: () => CloudflarePage() },
    { path: '/aws', view: () => AwsPage() },
    { path: '/docker', view: () => DockerPage() },
  ]},
];

const app = AppShell(
  Router(routes, () => NotFoundPage())
);

mount(app, document.getElementById('app')!);

// Intercept internal links so raw <a href="/..."> in page content uses client-side navigation
document.addEventListener('click', (e) => {
  const anchor = (e.target as HTMLElement).closest('a');
  if (!anchor) return;
  const href = anchor.getAttribute('href');
  if (!href || href.startsWith('http') || href.startsWith('#') || href.startsWith('mailto:')) return;
  e.preventDefault();
  navigate(href);
});

// Navigate to home if no hash
if (!location.hash || location.hash === '#/') {
  navigate('/');
}
